import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Plus, RefreshCw, Search } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { CAMP } from "@/constants/testIds";
import { useAuth, canCampaignsWrite } from "@/lib/auth";

const STATUS_CLS = {
  DRAFT: "bg-slate-100 text-slate-700",
  RUNNING: "bg-emerald-100 text-emerald-800",
  PAUSED: "bg-amber-100 text-amber-800",
  STOPPED: "bg-rose-100 text-rose-800",
  COMPLETED: "bg-sky-100 text-sky-800",
};

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

function CreateCampaignModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    expiry_days_window: 30,
    max_attempts: 3,
    retry_interval_hours: 24,
    customer_type: "",
    laptop_value_min: "",
    laptop_value_max: "",
    exclude_previously_renewed: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: "", description: "", expiry_days_window: 30,
        max_attempts: 3, retry_interval_hours: 24,
        customer_type: "", laptop_value_min: "", laptop_value_max: "",
        exclude_previously_renewed: false,
      });
      setErr(null);
    }
  }, [open]);

  if (!open) return null;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!form.name.trim()) { setErr("Name is required"); return; }
    const filters = {};
    if (form.customer_type) filters.customer_type = form.customer_type;
    if (form.laptop_value_min) filters.laptop_value_min = Number(form.laptop_value_min);
    if (form.laptop_value_max) filters.laptop_value_max = Number(form.laptop_value_max);
    if (form.exclude_previously_renewed) filters.exclude_previously_renewed = true;
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", {
        name: form.name.trim(),
        description: form.description || null,
        expiry_days_window: Number(form.expiry_days_window),
        max_attempts: Number(form.max_attempts),
        retry_interval_hours: Number(form.retry_interval_hours),
        filters,
      });
      onCreated(data.id);
    } catch (e) {
      setErr(errorToMessage(e, "Failed to create campaign"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4"
      data-testid={CAMP.modalOpen}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">New Renewal Campaign</h2>
          <p className="text-xs text-slate-500 mt-1">
            Enrol customers whose warranty is expiring inside the chosen window.
          </p>
        </div>
        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Name</span>
            <input value={form.name} onChange={(e) => upd("name", e.target.value)}
              data-testid={CAMP.modalName}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              placeholder="Q1-Renewal-Push" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Description</span>
            <textarea value={form.description} onChange={(e) => upd("description", e.target.value)}
              data-testid={CAMP.modalDescription} rows={2}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Expiry window (days)</span>
              <input type="number" min={1} max={365}
                value={form.expiry_days_window}
                onChange={(e) => upd("expiry_days_window", e.target.value)}
                data-testid={CAMP.modalWindow}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Max attempts</span>
              <input type="number" min={1} max={10}
                value={form.max_attempts}
                onChange={(e) => upd("max_attempts", e.target.value)}
                data-testid={CAMP.modalAttempts}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Retry interval (hrs)</span>
              <input type="number" min={1} max={168}
                value={form.retry_interval_hours}
                onChange={(e) => upd("retry_interval_hours", e.target.value)}
                data-testid={CAMP.modalRetryHours}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Eligibility filters (optional)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Customer type</span>
                <select value={form.customer_type} onChange={(e) => upd("customer_type", e.target.value)}
                  data-testid={CAMP.modalCustomerType}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                  <option value="">Any</option>
                  <option value="WALK_IN">Walk-in</option>
                  <option value="CORPORATE">Corporate</option>
                </select>
              </label>
              <label className="flex items-center gap-2 mt-6 text-sm text-slate-700">
                <input type="checkbox" checked={form.exclude_previously_renewed}
                  onChange={(e) => upd("exclude_previously_renewed", e.target.checked)}
                  data-testid={CAMP.modalExcludeRenewed} />
                Exclude previously renewed
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Min laptop value (₹)</span>
                <input type="number" min={0} value={form.laptop_value_min}
                  onChange={(e) => upd("laptop_value_min", e.target.value)}
                  data-testid={CAMP.modalMinValue}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder="0" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Max laptop value (₹)</span>
                <input type="number" min={0} value={form.laptop_value_max}
                  onChange={(e) => upd("laptop_value_max", e.target.value)}
                  data-testid={CAMP.modalMaxValue}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Any" />
              </label>
            </div>
          </div>
          {err && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {err}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} data-testid={CAMP.modalCancel}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid={CAMP.modalSubmit}
            className="rounded-md bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-60">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const params = useMemo(() => ({
    page, page_size: pageSize,
    status: status || undefined,
  }), [page, pageSize, status]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/campaigns", { params });
      setRows(data.items);
      setMeta(data.meta);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load campaigns"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const filtered = q
    ? rows.filter((r) => (r.name || "").toLowerCase().includes(q.toLowerCase()))
    : rows;

  return (
    <div className="space-y-6" data-testid={CAMP.page}>
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Warranty Renewal Campaigns</h1>
          <p className="text-sm text-slate-500 mt-1">
            Target customers with expiring warranties. Configurable pricing, retry policy, and manual payment-link generation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} data-testid={CAMP.refreshBtn}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          {canCampaignsWrite(user) && (
            <button type="button" onClick={() => setModalOpen(true)} data-testid={CAMP.createBtn}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800">
              <Plus size={14} /> New Campaign
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 shadow-sm">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid={CAMP.searchInput}
            placeholder="Search campaign name"
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid={CAMP.statusFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {["DRAFT", "RUNNING", "PAUSED", "STOPPED", "COMPLETED"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={CAMP.listTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Window (days)</th>
              <th className="text-right px-4 py-3">Total leads</th>
              <th className="text-right px-4 py-3">Renewed</th>
              <th className="text-left px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-400">
                <Megaphone size={32} className="inline text-slate-300 mb-2" />
                <div>No campaigns yet.</div>
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id} data-testid={CAMP.listRow(r.id)}
                onClick={() => navigate(`/admin/campaigns/${r.id}`)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="text-slate-900 font-medium">{r.name}</div>
                  {r.description && <div className="text-xs text-slate-500 line-clamp-1">{r.description}</div>}
                </td>
                <td className="px-4 py-3"><Chip text={r.status} cls={STATUS_CLS[r.status]} /></td>
                <td className="px-4 py-3 text-right font-mono text-xs">{r.expiry_days_window}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{Number(r.total_leads || 0)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">{Number(r.renewed_count || 0)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{meta.total} campaign{meta.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button data-testid={CAMP.pagePrev} disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Prev</button>
            <span>Page {page} of {meta.total_pages || 1}</span>
            <button data-testid={CAMP.pageNext} disabled={page >= (meta.total_pages || 1)}
              onClick={() => setPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      <CreateCampaignModal open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(id) => { setModalOpen(false); navigate(`/admin/campaigns/${id}`); }} />
    </div>
  );
}
